<!-- Loading Spinner -->
<div *ngIf="isLoading" class="loading-container">
    <mat-spinner diameter="50"></mat-spinner>
    <p>{{ 'projectdash.loading' | trans }}</p> 
</div>

<!-- Error Message -->
<div *ngIf="errorMessage" class="error-container">
    <mat-icon>error_outline</mat-icon>
    <p>{{ errorMessage }}</p>
</div>

<!-- Empty State -->
<div *ngIf="!isLoading && projectsPerMonth.length === 0 && !errorMessage" class="empty-state">
    <mat-icon>insert_chart</mat-icon>
    <p>{{ 'projectdash.nodata' | trans }}</p>
</div>

<!-- Dashboard Content -->
<div *ngIf="!isLoading && !errorMessage" class="container pl-1 pr-0">
    <div class="cards-grid">
        <!-- Card 1: Projects Per Month Bar Chart -->
        <div class="card">
            <h2 class="card-title">{{ 'projectdash.projectspermonth' | trans }}</h2>
            <div class="chart-container">
                <div class="bar-chart">
                    <div *ngFor="let data of projectsPerMonth" class="bar-item">
                        <div class="bar-column"
                            [style.height.%]="data.count > 0 ? (data.count / (getMaxProjectCount() || 1)) * 100 : 5"
                            [title]="data.month + ': ' + data.count + ' projects'">
                            <span class="bar-value">{{ data.count }}</span>
                        </div>
                        <span class="bar-label">{{ data.month }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Card 2: Execution Speed Circle Chart -->
        <div class="card">
            <h2 class="card-title">{{ 'projectdash.executionspeed' | trans }}</h2>
            <div class="chart-container">
                <div class="circle-chart">
                    <svg viewBox="0 0 200 200">
                        <defs>
                            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" style="stop-color:#2563eb;stop-opacity:1" />
                                <stop offset="100%" style="stop-color:#4f46e5;stop-opacity:1" />
                            </linearGradient>
                        </defs>
                        <circle class="circle-bg" cx="100" cy="100" r="80"></circle>
                        <circle class="circle-progress" cx="100" cy="100" r="80" [attr.stroke-dasharray]="502.65"
                            [attr.stroke-dashoffset]="502.65 - (executionSpeed?.speedPercent || 0) * 502.65 / 100">
                        </circle>
                    </svg>
                    <div class="circle-center">
                        <div class="circle-value">{{ executionSpeed?.speedPercent || 0 }}%</div>
                        <div class="circle-label">{{ 'projectdash.speed' | trans }}</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Card 3: Weekly Capacity Line Chart -->
        <div class="card">
            <h2 class="card-title">{{ 'projectdash.capacityvsplannedwork' | trans }}</h2>
            <div class="chart-container">
                <div class="line-chart" #lineChartRef>
                    <svg *ngIf="weeklyCapacity.length > 0" viewBox="0 0 500 200">
                        <defs>
                            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" style="stop-color:#222725;stop-opacity:1" />
                                <stop offset="100%" style="stop-color:#222725;stop-opacity:1" />
                            </linearGradient>
                            <linearGradient id="capacityGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" style="stop-color:#899878;stop-opacity:1" />
                                <stop offset="100%" style="stop-color:#899878;stop-opacity:1" />
                            </linearGradient>
                        </defs>

                        <!-- Grid lines -->
                        <line class="line-grid" x1="0" y1="44" x2="500" y2="44"></line>
                        <line class="line-grid" x1="0" y1="88" x2="500" y2="88"></line>
                        <line class="line-grid" x1="0" y1="132" x2="500" y2="132"></line>
                        <line class="line-grid" x1="0" y1="176" x2="500" y2="176"></line>

                        <!-- Capacity line (dashed) -->
                        <polyline [attr.points]="getCapacityLinePoints()" fill="none" stroke="#222725" stroke-width="3"
                            stroke-dasharray="8 4" stroke-linecap="round"></polyline>

                        <!-- Planned work line -->
                        <polyline [attr.points]="getPlannedWorkLinePoints()" class="line-path" fill="none"
                            stroke="#899878" stroke-width="3" stroke-linecap="round"></polyline>

                        <!-- Capacity data points -->
                        <g *ngFor="let day of weeklyCapacity; let i = index">
                            <circle [attr.cx]="getXPosition(i)" [attr.cy]="getYPosition(day.capacity)" r="4"
                                fill="#222725" stroke="#ffffff" stroke-width="2"></circle>
                            <circle [attr.cx]="getXPosition(i)" [attr.cy]="getYPosition(day.plannedWork)" r="4"
                                class="line-dot" (mouseenter)="showTooltip($event, i)" (mousemove)="moveTooltip($event)"
                                (mouseleave)="hideTooltip()"></circle>
                            <text [attr.x]="getXPosition(i)" y="190" class="day-label">
                                {{ 'projectdash.week' | trans }} {{ i + 1 }}
                            </text>
                        </g>
                    </svg>
                    <div *ngIf="tooltip.visible" class="line-tooltip" [style.left.px]="tooltip.x"
                        [style.top.px]="tooltip.y">
                        {{ tooltip.text }}
                    </div>
                    <div *ngIf="weeklyCapacity.length === 0" class="empty-state">
                        <p>{{ 'projectdash.nocapacitydata' | trans }}</p>
                    </div>
                </div>
            </div>
            <div class="chart-legend">
                <div class="legend-item">
                    <div class="legend-color" style="background: linear-gradient(135deg, #222725, #222725);"></div>
                    <span>{{ 'projectdash.capacity' | trans }}</span>
                </div>
                <div class="legend-item">
                    <div class="legend-color" style="background: linear-gradient(135deg, #899878, #899878);"></div>
                    <span>{{ 'projectdash.plannedwork' | trans }}</span>
                </div>
            </div>
        </div>
    </div>
</div>